import { storeToRefs } from 'pinia'

import { useComputeMismatches } from '~/composables/useComputeMismatches'

import { useDraftStore } from '~/stores/draft'
import { useDraftTrackStore } from '~/stores/draftTrack'
import { useDraftTrackTagsStore } from '~/stores/draftTrackTags'
import { useTagStore } from '~/stores/tag'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'

import { arrayUniqueValues } from '~/utils/arrays'

import type Tag from '~/entities/tag'
import type {
  Influencer,
  InfluencerLikedNeutralHatedTags,
  InfluencerPreferences,
  InfluencerPreferenceTags,
} from '~/types/influencer'
import type TagInformation from '~/types/tagInformations'

export function useCuratorMatchingTags(
  influencer: MaybeRefOrGetter<Influencer>,
) {
  const { t } = useI18n()
  const { mismatches } = useComputeMismatches('profile', () =>
    toValue(influencer),
  )

  const { tags: DRAFT_TAG_IDS } = storeToRefs(useDraftStore())
  const { BAND_DATA } = storeToRefs(useUserBandStore())
  const { IS_BAND } = storeToRefs(useUserStore())
  const { identity: DRAFT_TRACK_IDENTITY_TAGS } = storeToRefs(
    useDraftTrackTagsStore(),
  )
  const { release_date: DRAFT_TRACK_RELEASE_DATE } =
    storeToRefs(useDraftTrackStore())
  const { GET_TAGS_FROM_IDS, GET_TYPES_FROM_TAG_IDS } = useTagStore()

  const orderedTargets: readonly (keyof InfluencerPreferenceTags)[] = [
    'liked',
    'neutral',
    'hated',
  ]

  const genresAcceptedMostOftenIds = computed<number[]>(
    () => toValue(influencer).latest_stats?.genres_accepted_often ?? [],
  )

  const genresConfiguration = computed<InfluencerPreferences>(() => {
    if (!toValue(influencer).tags) {
      return {
        liked: [],
        neutral: [],
        hated: [],
      } satisfies InfluencerPreferences
    }

    return orderedTargets.reduce<InfluencerPreferences>((acc, target) => {
      const typeNames: (keyof InfluencerLikedNeutralHatedTags)[] = [
        'subgenre',
        'mood',
        'format',
        'artist_kind',
      ]
      const additionalTagInformations = typeNames.reduce<TagInformation[]>(
        (accumulator, typeName) => {
          const partialTagInformations = GET_TAGS_FROM_IDS([
            ...new Set(
              (toValue(influencer).tags[target] ?? {})[typeName] ?? [],
            ),
          ]).map((tag) => {
            return {
              typeName,
              tag,
            } satisfies TagInformation
          })
          if (!hasContent(partialTagInformations)) return accumulator

          return [...accumulator, ...partialTagInformations]
        },
        [],
      )
      if (!hasContent(additionalTagInformations)) return acc

      acc[target] = additionalTagInformations
      return acc
    }, {} as InfluencerPreferences)
  })

  const combinedInfluencerTagsIdsToMatch = computed<number[]>(() => {
    if (!genresConfiguration.value) return []

    const liked =
      genresConfiguration.value.liked?.filter(
        (tagInfo) => tagInfo.typeName === 'subgenre',
      ) ?? []
    const neutral =
      genresConfiguration.value.neutral?.filter(
        (tagInfo) => tagInfo.typeName === 'subgenre',
      ) ?? []

    return arrayUniqueValues([
      ...liked.map((tagInfo) => tagInfo.tag.id),
      ...neutral.map((tagInfo) => tagInfo.tag.id),
      ...genresAcceptedMostOftenIds.value,
    ])
  })

  const matchingLanguages = computed<Tag[]>(() => {
    const artistLanguages = DRAFT_TRACK_IDENTITY_TAGS.value.lyrics_lang || []
    const curatorLanguages =
      toValue(influencer).tags.exclusivity.lyrics_lang || []

    const curatorAcceptsAny = isAcceptAny(curatorLanguages)

    if (curatorAcceptsAny) return GET_TAGS_FROM_IDS(artistLanguages)

    const matches = artistLanguages.filter((tagId) =>
      curatorLanguages.includes(tagId),
    )

    return GET_TAGS_FROM_IDS(matches ?? [])
  })

  const combinedInfluencerTagsToMatch = computed<TagInformation[]>(() => {
    return GET_TAGS_FROM_IDS(combinedInfluencerTagsIdsToMatch.value).map(
      (tag) => {
        return { typeName: 'subgenre', tag }
      },
    )
  })

  const matchingGenres = computed<TagInformation[]>(() => {
    const artistGenres = GET_TAGS_FROM_IDS(
      BAND_DATA.value?.tags?.identity.subgenre || [],
    ).map((genre) => genre.id)

    const draftGenres = getMatchingDraftGenreTags()

    const matches = [...combinedInfluencerTagsToMatch.value].filter(
      (tagInfo) =>
        artistGenres.includes(tagInfo.tag.id) ||
        draftGenres.includes(tagInfo.tag.id),
    )

    return [...matches].filter((tagInfo) =>
      combinedInfluencerTagsIdsToMatch.value.includes(tagInfo.tag.id),
    )
  })

  const matchingNationalities = computed<Tag[]>(() => {
    const artistNationalities = BAND_DATA.value?.tags?.identity.country || []

    const curatorNationalities = toValue(influencer).tags?.exclusivity.country
    const curatorAcceptsAny = isAcceptAny(curatorNationalities)

    if (curatorAcceptsAny) return GET_TAGS_FROM_IDS(artistNationalities)

    const matches = artistNationalities.filter((tagId) =>
      curatorNationalities.includes(tagId),
    )

    return GET_TAGS_FROM_IDS(matches ?? [])
  })

  const matchingTrackRelease = computed<string>(() => {
    const curatorTrackRelease =
      GET_TAGS_FROM_IDS(toValue(influencer).tags.exclusivity.track_age)[0]
        ?.name || ''

    const trackReleaseDate = DRAFT_TRACK_RELEASE_DATE.value
      ? new Date(DRAFT_TRACK_RELEASE_DATE.value)
      : new Date()
    const isTrackReleased = new Date() > trackReleaseDate

    const acceptsAny = curatorTrackRelease === '_all_'
    const acceptsReleased =
      curatorTrackRelease === 'released-tracks' || acceptsAny
    const acceptsUnreleased =
      curatorTrackRelease === 'unreleased-tracks' || acceptsAny

    if (acceptsAny && !DRAFT_TRACK_RELEASE_DATE.value)
      return t(`tags.track_age._all_`)
    else if (acceptsReleased && isTrackReleased)
      return t(`tags.track_age.released-tracks`)
    else if (acceptsUnreleased && !isTrackReleased)
      return t(`tags.track_age.unreleased-tracks`)

    return ''
  })

  const hasMatches = computed<boolean>(
    () =>
      !!(
        matchingGenres.value.length > 0 ||
        matchingNationalities.value.length > 0 ||
        matchingLanguages.value.length > 0 ||
        matchingTrackRelease.value
      ),
  )

  const hasStrongMismatches = computed(() =>
    mismatches.value.some(({ weakMismatch }) => !weakMismatch),
  )

  const showMatchingTags = computed<boolean>(
    () => IS_BAND.value && !hasStrongMismatches.value && hasMatches.value,
  )

  function isAcceptAny(tags: number[]): boolean {
    return tags?.length === 1 && GET_TAGS_FROM_IDS(tags)[0]?.name === '_all_'
  }

  function getMatchingDraftGenreTags(): number[] {
    const genreTagTypes = GET_TYPES_FROM_TAG_IDS(
      DRAFT_TAG_IDS.value ?? [],
    ).filter((tagType) => tagType.name === 'subgenre')

    const genreTagIds = genreTagTypes.length > 0 ? genreTagTypes[0].tag_ids : []

    const draftGenreTagIds = DRAFT_TAG_IDS.value.filter((tagId) =>
      genreTagIds.includes(tagId),
    )

    const matchingDraftGenreTagIds = draftGenreTagIds.filter((tagId) =>
      combinedInfluencerTagsIdsToMatch.value.includes(tagId),
    )

    return matchingDraftGenreTagIds
  }

  function hasContent(tagInformations: TagInformation[]): boolean {
    return Boolean(tagInformations.length)
  }

  return {
    matchingGenres,
    matchingLanguages,
    matchingNationalities,
    matchingTrackRelease,
    showMatchingTags,
    mismatches,
    orderedTargets,
  }
}
